import { CopyOptions } from './types'

/**
 * Copies the provided text to the clipboard using the modern Clipboard API
 * with fallback to older methods for broader browser support.
 *
 * @param value - The text to copy to clipboard
 * @param options - Optional dependencies for testing purposes
 * @returns Promise<boolean> - Resolves to true if copying was successful
 * @throws Error if clipboard access is denied or not supported
 *
 * NOTE: This function must be called as a result of a user action (like a click)
 * to avoid "Write permission denied" errors. The Clipboard API requires user activation.
 */
export const copyToClipboard = async (
  value: string,
  options: CopyOptions = {},
): Promise<boolean> => {
  // Injected or global dependencies
  const clipboard = options.clipboard ?? navigator.clipboard
  const documentObj = options.documentObj ?? document
  const isSecureContext =
    options.isSecureContext ??
    (typeof window !== 'undefined' && window.isSecureContext)

  // Method 1: Try using Permissions API if available
  if (clipboard && isSecureContext && navigator.permissions) {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'clipboard-write' as PermissionName,
      })

      if (
        permissionStatus.state === 'granted' ||
        permissionStatus.state === 'prompt'
      ) {
        try {
          await clipboard.writeText(value)
          return true
        } catch {
          // If this fails, continue to fallback methods
        }
      }
    } catch {
      // Permissions API not supported - continue to next method
    }
  }

  // Method 2: Standard clipboard API in secure context
  if (clipboard && isSecureContext) {
    try {
      await clipboard.writeText(value)
      return true
    } catch {
      // If this fails, continue to fallback method
    }
  }

  // Method 3: Fallback for older browsers or non-HTTPS contexts
  const textArea = documentObj.createElement('textarea')
  try {
    textArea.value = value

    // Apply styles to hide the textarea from view
    textArea.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 2em;
      height: 2em;
      padding: 0;
      border: none;
      outline: none;
      background: transparent;
    `

    documentObj.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const execCommand =
      options.execCommand ??
      ((): ((command: string) => boolean) => {
        if (documentObj.execCommand) {
          return documentObj.execCommand.bind(documentObj)
        }
        return () => false
      })()

    const successful = execCommand('copy')
    if (!successful) {
      throw new Error('execCommand copy failed')
    }

    return true
  } catch {
    return false
  } finally {
    if (textArea && textArea.parentNode) {
      textArea.remove()
    }
  }
}
